import { useCallback, useEffect, useRef, useState } from 'react';
import { operatorConsole, type PoolOfferConfig } from '../../lib/api/operatorConsole';
import { mergeReadPacks } from './mergeReadPacks';

// Live packs for the preview, the first with artwork preselected. Re-read before a scene
// remount (mechanic switch, Reset preview): artwork URLs can be short-lived signed links.
export function usePreviewPacks(onError: (message: string) => void) {
  const [packs, setPacks] = useState<PoolOfferConfig[]>([]);
  const [packId, setPackId] = useState('');
  const packsRef = useRef(packs);
  const readSeq = useRef(0);
  // Openings this page wrote: a re-read from a replica or begun before the write must not undo them
  const written = useRef(new Map<number, PoolOfferConfig['animation']>());
  // The pack whose opening is being edited stays listed even when a re-read no longer has it
  const pinned = useRef('');

  const loadPacks = useCallback(async () => {
    const seq = ++readSeq.current;
    try {
      const resp = await operatorConsole.listPoolOfferConfigs({ availableOnly: true });
      if (seq !== readSeq.current) return;
      const next = mergeReadPacks(resp.pool_offer_configs, packsRef.current, written.current, pinned.current);
      packsRef.current = next;
      setPacks(next);
      setPackId((current) => {
        if (current && next.some((p) => String(p.pool_offer_id) === current)) return current;
        const first = next.find((p) => p.pack_image_url);
        return first ? String(first.pool_offer_id) : '';
      });
    } catch {
      if (seq === readSeq.current) onError('Failed to load packs');
    }
  }, [onError]);

  useEffect(() => {
    void loadPacks();
  }, [loadPacks]);

  const replacePack = useCallback((updated: PoolOfferConfig) => {
    written.current.set(updated.pool_offer_id, updated.animation);
    const next = packsRef.current.map((p) => (p.pool_offer_id === updated.pool_offer_id ? updated : p));
    packsRef.current = next;
    setPacks(next);
  }, []);

  const pinPack = useCallback((id: string) => {
    pinned.current = id;
  }, []);

  return { packs, packId, setPackId, loadPacks, replacePack, pinPack };
}
